import type { ReactElement } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import {
  PROMPT_SECTION_CATEGORIES,
  PROMPT_SECTION_CATEGORY_META,
  type PromptSectionCategory,
} from '../lib/promptSectionCategories';

interface Props {
  value: PromptSectionCategory[];
  onChange: (categories: PromptSectionCategory[]) => void;
  max?: number;
}

/* Every category is always on screen as a togglable chip, so picking several never closes anything and the
   colors match the ones the panel and the table use. */
const PromptSectionCategoriesPicker = ({ value, onChange, max = undefined }: Props): ReactElement => {
  const toggle = (category: PromptSectionCategory): void => {
    if (value.includes(category)) {
      onChange(value.filter((selected) => selected !== category));
      return;
    }
    if (max !== undefined && value.length >= max) return;
    onChange([...value, category]);
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {PROMPT_SECTION_CATEGORIES.map((category) => {
        const meta = PROMPT_SECTION_CATEGORY_META[category];
        const isSelected = value.includes(category);
        const isDisabled = !isSelected && max !== undefined && value.length >= max;

        return (
          <button
            key={category}
            type="button"
            disabled={isDisabled}
            onClick={() => toggle(category)}
            className={cn('rounded-4xl', isDisabled && 'cursor-not-allowed opacity-40')}
          >
            <Badge
              variant="outline"
              className={cn(isSelected ? meta.chip : 'border-border text-text-muted', !isDisabled && 'cursor-pointer')}
            >
              {meta.text}
            </Badge>
          </button>
        );
      })}
    </div>
  );
};

export default PromptSectionCategoriesPicker;
